import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, KeyRound, Search } from "lucide-react";
import { MBTI_GROUPS, mbtiInfo, clubInfo } from "@/lib/mbti";

const CLUBS = MBTI_GROUPS.map((g) => g.club);
const MBTI_CODES = MBTI_GROUPS.flatMap((g) => (g.types || []).map((t) => t.code));
const empty = { email: "", name: "", password: "", role: "consultant", club: "", coach_mbti: "", department: "", job_title: "" };

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const refresh = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { refresh(); }, []);

  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => {
      const hay = `${u.name || ""} ${u.email || ""} ${u.role || ""} ${u.club || ""} ${u.coach_mbti || ""} ${u.department || ""} ${u.job_title || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const isCoach = form.role === "coach";
      const canHaveJob = form.role === "staff" || form.role === "admin";
      if (editing) {
        const patch = {
          name: form.name,
          role: form.role,
          club: isCoach ? form.club || null : null,
          coach_mbti: isCoach ? (form.coach_mbti || null) : null,
          department: canHaveJob ? (form.department || null) : null,
          job_title: canHaveJob ? (form.job_title || null) : null,
        };
        if (form.password) patch.password = form.password;
        await api.put(`/users/${editing}`, patch);
        toast.success("Utilizador atualizado");
      } else {
        const payload = {
          email: form.email, name: form.name, password: form.password, role: form.role,
          club: isCoach ? form.club || null : null,
          coach_mbti: isCoach ? (form.coach_mbti || null) : null,
          department: canHaveJob ? (form.department || null) : null,
          job_title: canHaveJob ? (form.job_title || null) : null,
        };
        await api.post("/users", payload);
        toast.success("Utilizador criado");
      }
      setForm(empty); setEditing(null); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const edit = (u) => {
    setEditing(u.id);
    setForm({ email: u.email, name: u.name || "", password: "", role: u.role, club: u.club || "", coach_mbti: u.coach_mbti || "", department: u.department || "", job_title: u.job_title || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Eliminar este utilizador?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utilizador eliminado");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="users-page">
      <div>
        <div className="kw-overline mb-2">Administração</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">Utilizadores</h1>
      </div>

      <form onSubmit={submit} className="kw-card p-6 grid md:grid-cols-3 lg:grid-cols-6 gap-4 items-end" data-testid="user-form">
        <Field label="Email">
          <input type="email" className="kw-input" required disabled={!!editing} value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
        </Field>
        <Field label="Nome">
          <input className="kw-input" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
        </Field>
        <Field label={editing ? "Nova password (opc.)" : "Password"}>
          <input type="password" className="kw-input" required={!editing} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
        </Field>
        <Field label="Role">
          <select className="kw-input" value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value, club: e.target.value === "coach" ? form.club : "" })} data-testid="user-role">
            <option value="consultant">Consultor</option>
            <option value="coach">Secretário</option>
            <option value="staff">Staff</option>
            <option value="admin">Admin</option>
          </select>
        </Field>
        <Field label="Clube que coordena (Secretário)">
          <select className="kw-input" disabled={form.role !== "coach"} value={form.club}
            onChange={(e) => setForm({ ...form, club: e.target.value })} data-testid="user-club">
            <option value="">— escolher —</option>
            {CLUBS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="MBTI pessoal (Secretário)">
          <select className="kw-input" disabled={form.role !== "coach"} value={form.coach_mbti}
            onChange={(e) => setForm({ ...form, coach_mbti: e.target.value })} data-testid="user-coach-mbti">
            <option value="">— opcional —</option>
            {MBTI_CODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Departamento (Staff/Admin)">
          <input className="kw-input" disabled={form.role !== "staff" && form.role !== "admin"} value={form.department}
            placeholder="Marketing, Operações…"
            onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="user-department" />
        </Field>
        <Field label="Função (Staff/Admin)">
          <input className="kw-input" disabled={form.role !== "staff" && form.role !== "admin"} value={form.job_title}
            placeholder="Gestor(a) Plataformas, Recrutamento…"
            onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="user-job-title" />
        </Field>
        <div className="flex gap-2">
          <button className="kw-btn-primary inline-flex items-center gap-2" data-testid="user-submit">
            <Plus className="w-4 h-4" /> {editing ? "Guardar" : "Adicionar"}
          </button>
          {editing && (
            <button type="button" className="kw-btn-secondary" onClick={() => { setEditing(null); setForm(empty); }}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </form>

      <div className="kw-card overflow-x-auto">
        <div className="p-3 border-b border-[#222]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input
              type="text"
              className="kw-input !pl-10 !pr-10 w-full !py-2"
              placeholder="Procurar por nome, email, role, clube, MBTI, função…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="users-search" />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white" aria-label="Limpar pesquisa">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {q && (
            <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-2" data-testid="users-results-count">
              {filteredUsers.length} de {users.length} utilizadores
            </div>
          )}
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-3">Email</th>
              <th className="p-3">Nome</th>
              <th className="p-3">Role</th>
              <th className="p-3">Papel</th>
              <th className="p-3">Clube</th>
              <th className="p-3">MBTI</th>
              <th className="p-3">Função</th>
              <th className="p-3 text-right kw-th-sticky-col-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id} className="border-b border-[#1A1A1A] hover:bg-[#141414]">
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3 font-semibold">{u.name}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 text-[10px] uppercase tracking-widest font-bold border ${
                    u.role === "admin" ? "border-[#E50000]/40 text-[#FF6B6B] bg-[#E50000]/10" :
                    u.role === "coach" ? "border-white/40 text-white bg-white/5" :
                    "border-[#333] text-[#A3A3A3]"
                  }`}>{u.role}</span>
                </td>
                <td className="p-3">
                  {u.role_in_team === "assistant" ? (
                    <span className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold border border-[#FFB020]/60 text-[#FFB020] bg-[#FFB020]/10" title="Assistente — campeonato separado">Assistente</span>
                  ) : u.is_team_lead ? (
                    <span className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold border border-[#E50000] text-[#E50000]" title="Líder da equipa">Team Lead</span>
                  ) : u.role === "consultant" ? (
                    <span className="text-[#737373] text-xs">Consultor</span>
                  ) : (
                    <span className="text-[#737373] text-xs">—</span>
                  )}
                </td>
                <td className="p-3 text-[#A3A3A3]">{u.club || "—"}</td>
                <td className="p-3 text-[#A3A3A3]">{(() => {
                  if (!u.coach_mbti) return "—";
                  // Use the canonical color of the user's MBTI club (or the club they coordinate, as fallback)
                  const info = mbtiInfo(u.coach_mbti) || (u.club ? clubInfo(u.club) : null);
                  const color = info?.color || "#A3A3A3";
                  const border = info?.border || "#3331";
                  const bg = info?.bg || "transparent";
                  return (
                    <span
                      className="font-mono text-xs px-2 py-0.5 border inline-block"
                      style={{ color, borderColor: border, background: bg }}
                      title={info?.label || u.coach_mbti}
                      data-testid={`user-mbti-${u.id}`}>
                      {u.coach_mbti}
                    </span>
                  );
                })()}</td>
                <td className="p-3 text-[#A3A3A3] text-xs">
                  {u.job_title ? (
                    <div>
                      <div className="text-white">{u.job_title}</div>
                      {u.department && <div className="text-[10px] uppercase tracking-widest text-[#EAB308]">{u.department}</div>}
                    </div>
                  ) : u.department ? (
                    <span className="text-[10px] uppercase tracking-widest text-[#EAB308]">{u.department}</span>
                  ) : "—"}
                </td>
                <td className="p-3 text-right whitespace-nowrap kw-col-sticky-right">
                  <button onClick={() => edit(u)} className="kw-btn-secondary !py-1 !px-3 mr-2 inline-flex items-center gap-1" data-testid={`edit-user-${u.id}`}>
                    <Pencil className="w-3 h-3" /> Editar
                  </button>
                  <button onClick={() => del(u.id)} className="text-[#FF6B6B] hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold" data-testid={`del-user-${u.id}`}>
                    <Trash2 className="w-3 h-3" /> Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="kw-card p-5 border-[#E50000]/40 flex gap-3">
        <KeyRound className="w-5 h-5 text-[#E50000] flex-shrink-0 mt-1" />
        <div className="text-sm text-[#A3A3A3]">
          <div className="text-white font-display uppercase tracking-wider font-bold mb-1">Secretários default (passwords: <code>coach123</code>)</div>
          coach.analistas@kwlead.pt · coach.diplomatas@kwlead.pt · coach.sentinelas@kwlead.pt · coach.exploradores@kwlead.pt
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="kw-overline block mb-2">{label}</label>{children}</div>);
}
